// Copyright 2026 Epic Digital Interactive Media LLC
// SPDX-License-Identifier: Apache-2.0

import type { ClassLevelPermissions } from '../../types/schema'
export type { ClassLevelPermissions } from '../../types/schema'

/**
 * Default CLP applied to all built-in collections.
 * Matches current implicit behavior: authenticated users can read;
 * contributor role can create/update/delete.
 */
export const DEFAULT_CLP: ClassLevelPermissions = {
  create: { 'role:contributor': true },
  read: { authenticated: true },
  list: { authenticated: true },
  update: { 'role:contributor': true },
  delete: { 'role:manager': true },
  share: { 'role:owner': true },
  aclEnabled: false,
}

/** Collections that opt in to row-level ACL. */
export const ACL_ENABLED_COLLECTIONS = ['documents', 'records'] as const

/** Per-collection CLP overrides (merged over DEFAULT_CLP during seeding). */
export const COLLECTION_CLP_OVERRIDES: Record<string, Partial<ClassLevelPermissions>> = {
  documents: { aclEnabled: true },
  records: { aclEnabled: true },
  servicekeys: {
    create: { 'role:manager': true },
    read: { 'role:manager': true },
    list: { 'role:manager': true },
    update: { 'role:manager': true },
    delete: { 'role:manager': true },
    share: { 'role:owner': true },
    aclEnabled: true,
  },
  vaults: {
    create: { 'role:manager': true },
    read: { 'role:manager': true },
    list: { 'role:manager': true },
    update: { 'role:manager': true },
    delete: { 'role:manager': true },
    share: { 'role:owner': true },
    aclEnabled: true,
  },
  vaultitems: {
    create: { 'role:manager': true },
    read: { 'role:manager': true },
    list: { 'role:manager': true },
    update: { 'role:manager': true },
    delete: { 'role:manager': true },
    share: { 'role:owner': true },
    aclEnabled: true,
  },
  discord_mappings: {
    create: { 'role:manager': true },
    update: { 'role:manager': true },
  },
  discord_channels: {
    create: { 'role:manager': true },
    update: { 'role:manager': true },
  },
}

/**
 * Built-in collection names that receive a default CLP schema record.
 * These match the collection names registered in db-collections/src/index.ts.
 * RBAC collections and system collections (users, workers) are excluded.
 */
export const BUILT_IN_COLLECTION_NAMES: string[] = [
  'activities',
  'appsnapshots',
  'approvals',
  'attributes',
  'codebases',
  'conversations',
  'discussions',
  'documents',
  'goals',
  'messages',
  'milestones',
  'orgs',
  'processexecutions',
  'processsteps',
  'processes',
  'projects',
  'records',
  'relations',
  'schemas',
  'servicekeys',
  'steptemplates',
  'tasks',
  'teammembers',
  'templates',
  'timeentries',
  'vaults',
  'vaultitems',
  'workspaces',
  'discord_mappings',
  'discord_channels',
]
